// LiveKit browser client (ESM) with rich console logging
import * as LK from 'livekit-client';

/** Internal module state */
let room = null;                 // LK.Room instance
let localMicPub = null;          // LK.LocalTrackPublication for microphone
let currentAudioEls = new Set(); // <audio> elements created for remote audio
let isConnecting = false;

// 🔧 single switch to control BOTH:
// - local mic publishing
// - remote audio playback to the user's speakers
let audioIOEnabled = true;

// Single .NET sink (set during lk_connect if provided), always cleared on disconnect
let dotNetRef = null;            // DotNetObjectReference from .NET

/** Utilities */
const log = (...a) => console.log('[LK]', ...a);
const warn = (...a) => console.warn('[LK]', ...a);
const err = (...a) => console.error('[LK]', ...a);

/** Send connection state to .NET (bool only) */
function notifyDotNetConnected(connected) {
  if (!dotNetRef) return;
  try {
    dotNetRef.invokeMethodAsync('OnLiveKitConnectionChanged', !!connected);
  } catch (e) {
    warn('dotnet notify failed', e);
  }
}

export function lk_connectionSnapshot() {
  const state = room?.state ?? LK.ConnectionState.Disconnected;
  return {
    state,
    connected: state === LK.ConnectionState.Connected,
    isConnecting:
      state === LK.ConnectionState.Connecting ||
      state === LK.ConnectionState.Reconnecting ||
      isConnecting,
  };
}

/** Config controls */
export function lk_setAudioIOEnabled(enabled) {
  audioIOEnabled = !!enabled;
  log('⚙️ audioIOEnabled =', audioIOEnabled);
  // If disabling while connected: stop mic & stop any playing remote audio locally
  if (room && !audioIOEnabled) {
    room.localParticipant.setMicrophoneEnabled(false).catch(() => { });
    cleanupAudioEls();
  }
}
export function lk_isAudioIOEnabled() {
  return !!audioIOEnabled;
}

/** Optional helpers for the .NET side */
export function lk_isConnected() {
  return !!room && room.state === LK.ConnectionState.Connected;
}

/** Attach a remote audio track, autoplay and wire end/pause logs */
function playRemoteAudioTrack(track /* RemoteAudioTrack */) {
  if (!audioIOEnabled) {
    log('🔇 audioIO disabled: not attaching remote audio to speakers');
    return;
  }
  try {
    const el = track.attach();
    el.autoplay = true;

    el.addEventListener('play', () => log('🔊 remote audio PLAY'));
    el.addEventListener('pause', () => log('⏸️ remote audio PAUSE'));
    el.addEventListener('ended', () => log('🔇 remote audio ENDED'));

    // Try to play; browsers may block until user-gesture
    el.play().catch(() => warn('autoplay blocked; will start after user gesture'));
    currentAudioEls.add(el);
  } catch (e) {
    err('failed to attach remote audio', e);
  }
}

/** Cleanup all created <audio> elements */
function cleanupAudioEls() {
  for (const el of currentAudioEls) {
    try {
      el.pause();
      el.srcObject = null;
      el.remove();
    } catch { }
  }
  currentAudioEls.clear();
}

/** Wire up LiveKit Room logs + notify .NET on key transitions */
function wireRoomLogs(r /* LK.Room */) {
  r
    .on(LK.RoomEvent.Connected, () => {
      log('🔗 Connected');
      notifyDotNetConnected(true);
    })
    .on(LK.RoomEvent.Disconnected, (reason) => {
      log('🔌 Disconnected', reason);
      notifyDotNetConnected(false);
      // Always clear the .NET sink on disconnect
      dotNetRef = null;
    })
    .on(LK.RoomEvent.Reconnecting, () => {
      log('… Reconnecting');
      // no .NET notify; only care about connected true/false
    })
    .on(LK.RoomEvent.Reconnected, () => {
      log('✅ Reconnected');
      notifyDotNetConnected(true);
    })

    // Participants
    .on(LK.RoomEvent.ParticipantConnected, (p) =>
      log('🧑‍🤝‍🧑 ParticipantConnected', p.identity))
    .on(LK.RoomEvent.ParticipantDisconnected, (p) =>
      log('👋 ParticipantDisconnected', p.identity))

    // Tracks (remote)
    .on(LK.RoomEvent.TrackPublished, (pub, p) =>
      log('📢 TrackPublished', pub.kind, 'by', p.identity))
    .on(LK.RoomEvent.TrackUnpublished, (pub, p) =>
      log('📴 TrackUnpublished', pub.kind, 'by', p.identity))
    .on(LK.RoomEvent.TrackSubscribed, (track, pub, p) => {
      log('📡 TrackSubscribed', track.kind, 'from', p.identity);
      if (track.kind === LK.Track.Kind.Audio) {
        playRemoteAudioTrack(track);
        if (audioIOEnabled) log('🤖 AI voice received (remote audio attached)');
      }
    })
    .on(LK.RoomEvent.TrackUnsubscribed, (track, pub, p) =>
      log('📡 TrackUnsubscribed', track?.kind, 'from', p.identity))
    .on(LK.RoomEvent.TrackSubscriptionFailed, (pub, p) =>
      warn('⚠️ TrackSubscriptionFailed', pub?.kind, 'from', p?.identity))
    .on(LK.RoomEvent.TrackMuted, (pub, p) =>
      log('🔇 TrackMuted', pub?.kind, 'by', p?.identity))
    .on(LK.RoomEvent.TrackUnmuted, (pub, p) =>
      log('🔊 TrackUnmuted', pub?.kind, 'by', p?.identity))

    // Local tracks
    .on(LK.RoomEvent.LocalTrackPublished, (pub) =>
      log('📤 LocalTrackPublished', pub.kind, pub.trackName))
    .on(LK.RoomEvent.LocalTrackUnpublished, (pub) =>
      log('📤 LocalTrackUnpublished', pub.kind, pub.trackName))

    // Active speakers
    .on(LK.RoomEvent.ActiveSpeakersChanged, (speakers) =>
      log('📢 ActiveSpeakersChanged', speakers.map(s => s.identity)))

    // Transcriptions (from server-side ASR)
    .on(LK.RoomEvent.TranscriptionReceived, (segments, participant) => {
      const who = participant?.identity ?? 'unknown';
      try {
        segments.forEach((seg) => {
          const isFinal = (seg && typeof seg.final === 'boolean') ? seg.final : false;
          const text = seg?.text ?? '';

          // 🔔 forward each segment to .NET using the helper
          notifyDotNetTranscription(text, isFinal, who);

          // keep console logs (optional)
          const tag = isFinal ? '📝 FINAL' : '⌨️ INTERIM';
          log(`${tag} transcription from ${who}:`, text);
        });
      } catch (e) {
        warn('TranscriptionReceived handler error:', e, segments);
      }
    })

    // Room state (keep logs; .NET only cares about connected flips)
    .on(LK.RoomEvent.ConnectionStateChanged, (state) =>
      log('🔁 ConnectionStateChanged:', state))
    .on(LK.RoomEvent.ParticipantMetadataChanged, (p) =>
      log('🧾 ParticipantMetadataChanged', p.identity, p.metadata))
    .on(LK.RoomEvent.AudioPlaybackStatusChanged, (playing) =>
      log('🔈 AudioPlaybackStatusChanged =>', playing ? 'playing' : 'blocked'))
    .on(LK.RoomEvent.SignalConnected, () => log('📶 SignalConnected'))
    .on(LK.RoomEvent.SignalReconnected, () => log('📶 SignalReconnected'));
}

/**
 * Connect to a LiveKit room.
 * @param {string} url   - server ws URL
 * @param {string} token - access token
 * @param {object} [opts]
 * @param {boolean} [opts.autoEnableMic=true] - enable mic (ignored if audioIOEnabled=false)
 * @param {boolean} [opts.audioIOEnabled] - override global audioIOEnabled at connect-time
 * @param {any}     [opts.dotNetRef] - DotNetObjectReference to receive connection updates
 */
export async function lk_connect(url, token, opts = {}) {
  if (room) {
    warn('already connected; call lk_disconnect() first');
    return true;
  }

  // per-connection override
  if (typeof opts.audioIOEnabled === 'boolean') {
    lk_setAudioIOEnabled(opts.audioIOEnabled);
  }
  const wantAutoMic = !!(opts.autoEnableMic ?? true);

  // .NET sink hookup (optional). Always cleared when disconnected.
  dotNetRef = opts.dotNetRef || null;
  // push snapshot immediately
  try {
    const snap = lk_connectionSnapshot();
    if (dotNetRef) notifyDotNetConnected(snap.connected);
  } catch { }

  try {
    isConnecting = true;
    log('🚀 connecting…', { url, audioIOEnabled, wantAutoMic });

    room = new LK.Room();
    wireRoomLogs(room);

    await room.connect(url, token);
    log('✅ connected to room', { sid: room.sid });
    notifyDotNetConnected(true);

    if (audioIOEnabled) {
      try {
        await room.startAudio();
        log('🔈 startAudio() ok (autoplay unlocked)');
      } catch {
        warn('🔈 startAudio() blocked until user gesture');
      }
    }

    if (audioIOEnabled && wantAutoMic) {
      await lk_setMicEnabled(true);
    } else {
      log('🎙️ mic NOT auto-enabled (audioIOEnabled=', audioIOEnabled, 'autoEnableMic=', wantAutoMic, ')');
    }

    isConnecting = false;
    return true;
  } catch (e) {
    isConnecting = false;
    err('connect failed', e);
    notifyDotNetConnected(false);
    try { await lk_disconnect(); } catch { }
    return false;
  }
}

/** Disconnect and cleanup. Safe to call multiple times. */
export async function lk_disconnect() {
  try {
    if (room) {
      log('🔌 disconnecting…');
      cleanupAudioEls();
      await room.disconnect();
    }
  } catch (e) {
    warn('disconnect error (ignored)', e);
  } finally {
    room = null;
    localMicPub = null;
    isConnecting = false;
    notifyDotNetConnected(false);

    // Always clear .NET sink on disconnect
    dotNetRef = null;
    log('🪝 .NET connection sink cleared');
  }
}

/**
 * Enable/disable microphone.
 * - Respects audioIOEnabled (will refuse to enable if disabled).
 */
export async function lk_setMicEnabled(enabled) {
  if (!room) {
    warn('setMicEnabled called without a room');
    return false;
  }
  // if (enabled && !audioIOEnabled) {
  //   warn('audioIOEnabled=false: refusing to enable mic publish');
  //   return false;
  // }
  try {
    await room.localParticipant.setMicrophoneEnabled(enabled);
    const pubs = Array.from(room.localParticipant.audioTrackPublications.values());
    localMicPub = pubs.length ? pubs[0] : null;

    if (localMicPub?.track) {
      localMicPub.track
        .on('muted', () => log('🔇 local mic muted'))
        .on('unmuted', () => log('🔊 local mic unmuted'))
        .on('ended', () => log('⏹️ local mic ended'));
    }

    log('🎙️ microphone', enabled ? 'ENABLED' : 'DISABLED');
    return true;
  } catch (e) {
    err('setMicrophoneEnabled failed', e);
    return false;
  }
}

/**
 * Send a text message over LiveKit data channel.
 * (Kept for API parity; relies on SDK’s sendText/publishData.)
 */
export async function lk_sendText(payload, opts) {
  if (!room) {
    warn('sendText called without a room');
    return false;
  }

  // Defaults live HERE
  const topic = opts?.topic ?? 'lk.chat';
  const reliable = opts?.reliable ?? true;

  // Ensure we send plain text to match the server’s expectation
  const text = typeof payload === 'string' ? payload : JSON.stringify(payload);

  try {
    if (typeof room.localParticipant?.sendText === 'function') {
      await room.localParticipant.sendText(text, { topic, reliable });
    } else {
      const data = new TextEncoder().encode(text); // fallback for older SDKs
      await room.localParticipant.publishData(data, { topic, reliable });
    }
    log('📤 TextPublished', { topic, reliable, bytes: text.length });

    // 🔔 NEW: Manually trigger the transcription event for the local user's own text
    // We treat typed text as "Final" immediately.
    const who = room.localParticipant?.identity ?? 'local-user';
    notifyDotNetTranscription(text, true, who);

    return true;
  } catch (e) {
    err('sendText/publishData failed', e);
    return false;
  }
}

/** Handy for diagnostics in devtools. */
export function lk_debugState() {
  if (!room) {
    log('state: no room');
    return;
  }
  log('state snapshot', {
    isConnecting,
    audioIOEnabled,
    roomSid: room.sid,
    name: room.name,
    participants: Array.from(room.participants.values()).map(p => ({
      id: p.identity,
      audio: Array.from(p.audioTrackPublications.values()).map(pub => pub.trackName),
    })),
    localTracks: Array.from(room.localParticipant.tracks.values()).map(pub => ({
      kind: pub.kind,
      name: pub.trackName,
      muted: pub.isMuted,
    })),
  });
}

/** Send transcription/text data to .NET */
function notifyDotNetTranscription(text, isFinal, who) {
  if (!dotNetRef || !text) return;
  try {
    // Notify .NET subscribers (e.g., for UI display or processing)
    dotNetRef.invokeMethodAsync('OnTranscriptionReceived', text, isFinal, who);
  } catch (e) {
    warn('dotnet OnTranscriptionReceived failed', e);
  }
}

if (typeof window !== 'undefined') {
  window.LKInterop = {
    lk_connect,
    lk_disconnect,
    lk_setMicEnabled,
    lk_setAudioIOEnabled,
    lk_isConnected,
    lk_sendText,
    lk_debugState,
  };
}
